import { zodResolver } from "@hookform/resolvers/zod";
import { useTranslation } from "next-i18next";
import { FieldErrorsImpl, FieldValues, useForm } from "react-hook-form";
import { z } from "zod";

import { ContactFormFields } from "@/api/contact-us/types";
import { Accordion } from "@/components/elements/Accordion";
import { Form } from "@/components/elements/Form";
import { InputText } from "@/components/elements/InputText";
import { Text } from "@/components/elements/Text";
import { useContactUs } from "@/hooks/contact-us";
import useBreakpointTest from "@/hooks/useBreakpointTest";

import { TextAreaInput } from "../elements/TextAreaInput";
import styles from "./styles.module.css";

export default function ContactForm() {
  const { t } = useTranslation("contact");
  const isDesktop = useBreakpointTest("lg");
  const { mutate, error: serverError, isLoading, isSuccess } = useContactUs();

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<FieldValues>({
    mode: "onSubmit",
    criteriaMode: "all",
    defaultValues: {},
    shouldFocusError: true,
    resolver: zodResolver(
      z.object({
        name: z
          .string()
          .min(1, t("error_required", { ns: "common" }))
          .regex(/^([^0-9]*)$/, t("error_letters_only", { ns: "common" })),
        email: z
          .string()
          .regex(
            /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
            t("error_email_only", { ns: "common" })
          ),
        message: z.string().min(1, t("error_required", { ns: "common" })),
        topic: z.string().min(1, t("error_required", { ns: "common" })),
      })
    ),
  });

  const onSubmit = (contactFormData: ContactFormFields) => {
    mutate(contactFormData, {
      onSuccess: () => {
        reset();
      },
    });
  };

  return (
    <Accordion
      className={styles.contactUsFormContainer}
      title={
        <div>
          <Text disableMarginBottom size="m-20" as="h2">
            {t("contact_form_title")}
          </Text>
        </div>
      }
      version="mini"
      forceOpen={isDesktop}
    >
      <Form
        className={styles.contactForm}
        onSubmit={handleSubmit(onSubmit)}
        primaryButtonText={t("contact_form_cta")}
        serverError={serverError}
        primaryButtonTheme={"dark"}
        disabled={isLoading}
      >
        <InputText
          name={"name"}
          label={t("contact_form_label_name")}
          version={"box"}
          theme={"dark"}
          id={"name"}
          inputClassName={styles.infoInputField}
          key={`name-field`}
          type={"text"}
          register={register}
          errors={errors as FieldErrorsImpl<FieldValues>}
        />
        <InputText
          name={"email"}
          label={t("contact_form_label_email")}
          type={"email"}
          version={"box"}
          theme={"dark"}
          id={"email-field"}
          inputClassName={styles.infoInputField}
          key={`email-field`}
          register={register}
          errors={errors as FieldErrorsImpl<FieldValues>}
        />

        <InputText
          register={register}
          name={"topic"}
          label={t("contact_form_label_topic")}
          type={"text"}
          version={"box"}
          theme={"dark"}
          id={"topic-field"}
          key={"topic-field"}
          inputClassName={styles.infoInputField}
          errors={errors as FieldErrorsImpl<FieldValues>}
        />

        <TextAreaInput
          register={register}
          name="message"
          label={t("contact_form_label_message")}
          version={"box"}
          theme={"dark"}
          id={"message-field"}
          key={"message-field"}
          inputClassName={styles.messageField}
          errors={errors as FieldErrorsImpl<FieldValues>}
        />
        {isSuccess && (
          <Text className={styles.copySuccess}>
            {t("contact_form_confirmation_message")}
          </Text>
        )}
      </Form>
    </Accordion>
  );
}
